// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/machine-group/MachineGroupListPage.jsx
// 📝 Purpose:
//   Machine Group Master – List Page
//   - Gradient header
//   - KPI-ready (future)
//   - Search + status filter
//   - Table view
//   - Create / Edit Drawer
// ======================================================================

import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";

import axiosClient from "../../../../../api/axiosClient";
import { AuthContext } from "../../../../../context/AuthContext";

import MachineGroupDrawer from "./MachineGroupDrawer";
import MachineGroupKpiBar from "../../../components/machine-group/MachineGroupKpiBar";

// ----------------------------------------------------------------------
export default function MachineGroupListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  // ------------------------------------------------------------
  // Load machine groups
  // ------------------------------------------------------------
  const loadMachineGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/machine-groups"
      );
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load machine groups", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMachineGroups();
  }, [loadMachineGroups]);

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Filtering
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.group_code?.toLowerCase().includes(q) ||
      r.group_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" ||
      (status === "ACTIVE" && r.is_active) ||
      (status === "INACTIVE" && !r.is_active);

    return matchesSearch && matchesStatus;
  });

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Machine Groups</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Group machines for routing and planning
          </div>
        </div>

        {permissions?.MACHINE_GROUP_CREATE?.create && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Machine Group
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <MachineGroupKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadMachineGroups}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 160 }}>Group Code</th>
                <th>Group Name</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center text-muted"
                  >
                    No machine groups found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.group_code}</td>
                  <td>{r.group_name}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    {permissions?.MACHINE_GROUP_EDIT?.edit && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Drawer */}
      {/* ====================================================== */}
      <MachineGroupDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={loadMachineGroups}
      />
    </div>
  );
}
